/* ============================================================
   REVIVIFEYE — Hero Split Carousel
   Vanilla JS · No dependencies · Shopify theme editor safe
   ============================================================ */

class HeroSplitCarousel {
  constructor(section) {
    this.section   = section;
    this.trackId   = section.id.replace('hero-split-', 'hero-track-');
    this.track     = section.querySelector('.hero-split__track');
    this.slides    = Array.from(section.querySelectorAll('.hero-split__slide'));
    this.dots      = Array.from(section.querySelectorAll('.hero-split__dot'));
    this.prevBtn   = section.querySelector('.hero-split__arrow--prev');
    this.nextBtn   = section.querySelector('.hero-split__arrow--next');
    this.current   = 0;
    this.total     = this.slides.length;
    this.timer     = null;
    this.touchStartX = 0;

    if (this.total <= 1) return;

    this._bindEvents();
    this._startAutoplay();
  }

  goTo(index) {
    const prev = this.current;
    this.current = ((index % this.total) + this.total) % this.total;
    if (prev === this.current) return;

    // Deactivate previous slide
    this.slides[prev].classList.remove('is-active');
    this.slides[prev].setAttribute('aria-hidden', 'true');
    if (this.dots[prev]) {
      this.dots[prev].classList.remove('is-active');
      this.dots[prev].setAttribute('aria-selected', 'false');
    }

    // Activate new slide
    this.slides[this.current].classList.add('is-active');
    this.slides[this.current].setAttribute('aria-hidden', 'false');
    if (this.dots[this.current]) {
      this.dots[this.current].classList.add('is-active');
      this.dots[this.current].setAttribute('aria-selected', 'true');
    }
  }

  _bindEvents() {
    // Arrow buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.goTo(this.current - 1);
        this._resetAutoplay();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.goTo(this.current + 1);
        this._resetAutoplay();
      });
    }

    // Dot buttons
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.goTo(i);
        this._resetAutoplay();
      });
    });

    // Keyboard navigation
    this.section.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { this.goTo(this.current - 1); this._resetAutoplay(); }
      if (e.key === 'ArrowRight') { this.goTo(this.current + 1); this._resetAutoplay(); }
    });

    // Touch / swipe
    this.section.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].clientX;
    }, { passive: true });

    this.section.addEventListener('touchend', (e) => {
      const delta = this.touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(delta) > 48) {
        this.goTo(delta > 0 ? this.current + 1 : this.current - 1);
        this._resetAutoplay();
      }
    }, { passive: true });

    // Pause on hover
    this.section.addEventListener('mouseenter', () => this._stopAutoplay());
    this.section.addEventListener('mouseleave', () => this._startAutoplay());

    // Pause when tab is hidden
    document.addEventListener('visibilitychange', () => {
      document.hidden ? this._stopAutoplay() : this._startAutoplay();
    });
  }

  _startAutoplay() {
    this._stopAutoplay();
    this.timer = setInterval(() => this.goTo(this.current + 1), 5000);
  }

  _stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  _resetAutoplay() {
    this._stopAutoplay();
    this._startAutoplay();
  }

  destroy() {
    this._stopAutoplay();
  }
}

/* ── Init / Shopify editor lifecycle ─────────────────────── */

const heroInstances = new Map();

function initHeroCarousels() {
  document.querySelectorAll('.hero-split').forEach((el) => {
    if (!heroInstances.has(el.id)) {
      heroInstances.set(el.id, new HeroSplitCarousel(el));
    }
  });
}

// Initial load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeroCarousels);
} else {
  initHeroCarousels();
}

// Shopify theme editor — section reloaded
document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('.hero-split');
  if (!section) return;
  // Destroy old instance if exists, then re-init
  if (heroInstances.has(section.id)) {
    heroInstances.get(section.id).destroy();
    heroInstances.delete(section.id);
  }
  heroInstances.set(section.id, new HeroSplitCarousel(section));
});

// Shopify theme editor — section unloaded
document.addEventListener('shopify:section:unload', (e) => {
  const section = e.target.querySelector('.hero-split');
  if (!section) return;
  if (heroInstances.has(section.id)) {
    heroInstances.get(section.id).destroy();
    heroInstances.delete(section.id);
  }
});

// Shopify theme editor — block selected (jump to slide)
document.addEventListener('shopify:block:select', (e) => {
  const slide = e.target;
  if (!slide.classList.contains('hero-split__slide')) return;
  const section = slide.closest('.hero-split');
  if (!section || !heroInstances.has(section.id)) return;
  const instance = heroInstances.get(section.id);
  const index = instance.slides.indexOf(slide);
  if (index >= 0) {
    instance._stopAutoplay();
    instance.goTo(index);
  }
});
